import { useState } from 'react';
import { MessageSquarePlus, X, Send, Image as ImageIcon, CheckCircle, RefreshCcw, AlertTriangle, Pencil } from 'lucide-react';

import { collection, addDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { FeedbackAnnotation } from './FeedbackAnnotation';

export function AsyncFeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [screenshotData, setScreenshotData] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [isRecapturingAngle, setIsRecapturingAngle] = useState(false);

  const captureScreen = async () => {
    setIsCapturing(true);
    setError(null);
    try {
      // Find the main WebGL canvas
      const canvas = document.querySelector('canvas');
      if (canvas) {
        // Create an offscreen canvas to resize and convert to JPEG for faster upload
        const offscreenCanvas = document.createElement('canvas');
        const ctx = offscreenCanvas.getContext('2d');
        if (!ctx) throw new Error("Contexto 2D não suportado");

        offscreenCanvas.width = canvas.width;
        offscreenCanvas.height = canvas.height;
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(canvas, 0, 0);
        const dataUrl = offscreenCanvas.toDataURL('image/png', 1.0);
        setScreenshotData(dataUrl);
      } else {
        throw new Error("Canvas não encontrado.");
      }
    } catch (err: any) {
      console.error("Erro ao capturar tela:", err);
      setError("Falha ao capturar a tela 3D. Tente novamente.");
    } finally {
      setIsCapturing(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setUploadSuccess(false);
    setError(null);
    setDescription('');
    setShowCancelConfirm(false);
    setShowSubmitConfirm(false);
    captureScreen();
  };

  const handleClose = () => {
    if (description.trim() !== '' && !showCancelConfirm && !uploadSuccess) {
      setShowCancelConfirm(true);
      return;
    }
    setIsOpen(false);
    setScreenshotData(null);
    setDescription('');
    setError(null);
    setShowCancelConfirm(false);
    setShowSubmitConfirm(false);
    setIsAnnotating(false);
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError("Por favor, descreva seu feedback antes de enviar.");
      return;
    }

    if (!showSubmitConfirm) {
      setShowSubmitConfirm(true);
      return;
    }

    if (!auth.currentUser) {
      setError("Você precisa estar logado para enviar feedback.");
      return;
    }
    
    if (!screenshotData) {
      setError("Aguarde a captura de tela ou tente novamente.");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Compress the image before sending
      let finalBase64 = screenshotData;
      const img = new Image();
      img.src = screenshotData;
      await new Promise((resolve) => {
        img.onload = () => {
          const cvs = document.createElement('canvas');
          const maxW = 800;
          const maxH = 600;
          let w = img.width;
          let h = img.height;
          if (w > h) {
            if (w > maxW) { h *= maxW / w; w = maxW; }
          } else {
            if (h > maxH) { w *= maxH / h; h = maxH; }
          }
          cvs.width = w; cvs.height = h;
          const context = cvs.getContext('2d');
          if (context) {
            context.fillStyle = '#000000';
            context.fillRect(0, 0, w, h);
            context.drawImage(img, 0, 0, w, h);
            finalBase64 = cvs.toDataURL('image/jpeg', 0.7);
          }
          resolve(null);
        };
      });

      // Create firestore document with base64 image data directly
      await addDoc(collection(db, 'feedbacks'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || 'Desconhecido',
        imageBase64: finalBase64,
        description: description,
        createdAt: new Date().toISOString(),
        status: 'new'
      });

      setUploadSuccess(true);
    } catch (err: any) {
      console.error("Erro ao enviar feedback:", err);
      setError(`Falha ao enviar: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  if (isRecapturingAngle) {
    return (
      <div className="fixed bottom-10 left-1/2 -translate-x-1/2" style={{ zIndex: 99999 }}>
        <button
          onClick={() => {
            setIsRecapturingAngle(false); window.dispatchEvent(new CustomEvent('feedback-recapture', { detail: false }));
            setTimeout(() => {
              captureScreen().then(() => setIsOpen(true));
            }, 300);
          }}
          className="px-6 py-3 bg-[#00ffa3] hover:bg-[#00ffa3]/80 text-black font-bold uppercase tracking-widest rounded-full shadow-[0_0_25px_rgba(0,255,163,0.5)] flex items-center gap-2 text-xs md:text-sm transition-all"
        >
          <RefreshCcw size={16} />
          Fazer nova Captura
        </button>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="fixed top-20 md:top-24 right-4 md:right-6 z-[40] bg-[#00ffa3]/10 hover:bg-[#00ffa3]/20 border border-[#00ffa3]/30 hover:border-[#00ffa3]/50 text-[#00ffa3] p-3 md:py-2 md:px-4 rounded-full backdrop-blur-md transition-all shadow-[0_0_15px_rgba(0,255,163,0.15)] group flex items-center gap-2 animate-pulse hover:animate-none"
        title="Enviar Feedback"
      >
        <MessageSquarePlus size={20} />
        <span className="text-[10px] font-bold uppercase tracking-wider hidden md:block">
          Deixar Feedback
        </span>
      </button>
    );
  }

  return (
    <div style={{ zIndex: 999999 }} className="fixed inset-0 flex items-center justify-center bg-[#02040a] md:bg-black/90 md:backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-[#02040a] md:bg-[#050505] md:border border-white/10 p-4 md:p-6 w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl md:rounded-lg flex flex-col shadow-2xl relative overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 text-white/40 hover:text-white transition-colors z-10 bg-black/50 p-1 rounded-full md:bg-transparent"
        >
          <X size={20} />
        </button>

        <h2 className="text-[#00ffa3] text-xs md:text-sm uppercase tracking-[2px] border-b border-[#00ffa3]/20 pb-3 mb-4 flex items-center gap-2 shrink-0 mt-2 md:mt-0">
          <MessageSquarePlus size={16} />
          Feedback Visual
        </h2>

        {uploadSuccess ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4 text-center flex-1">
            <CheckCircle size={48} className="text-[#00ffa3] animate-pulse" />
            <div className="text-white font-mono text-sm">Feedback enviado com sucesso!</div>
            <div className="text-white/50 text-xs">A equipe receberá sua captura e notas de forma segura.<br />Obrigado pelo seu feedback.</div>
            <button
              onClick={() => {
                setIsOpen(false);
                setScreenshotData(null);
                setDescription('');
                setError(null);
                setShowCancelConfirm(false);
                setShowSubmitConfirm(false);
                setIsAnnotating(false);
                setUploadSuccess(false);
              }}
              className="mt-4 px-6 py-2 rounded bg-[#00ffa3]/20 hover:bg-[#00ffa3]/30 text-[#00ffa3] border border-[#00ffa3]/50 font-bold uppercase tracking-wider text-xs transition-all"
            >
              Concluir
            </button>
          </div>
        ) : (
          <div className="flex flex-col flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 flex-1 min-h-0">
              <div className="flex flex-col gap-2">
                <div className="text-[10px] md:text-xs uppercase tracking-wider text-white/40 flex justify-between">
                  <span>Captura de Tela</span>
                  {isCapturing && <span className="text-[#00ffa3] animate-pulse">Capturando...</span>}
                </div>
                <div className="bg-black border border-white/5 rounded overflow-hidden aspect-video md:aspect-auto md:flex-1 relative flex flex-col items-center justify-center min-h-[150px]">
                  {screenshotData ? (
                    <>
                      <img src={screenshotData} alt="Screenshot" className="w-full h-full object-contain flex-1" />
                      <div className="absolute bottom-2 inset-x-0 flex items-center justify-center gap-2 z-10 px-2">
                        <button 
                          onClick={() => setIsAnnotating(true)}
                          className="px-3 py-2 bg-black/80 border border-white/20 hover:border-[#00ffa3]/50 text-[#00ffa3] text-[10px] md:text-xs font-bold uppercase tracking-wider rounded backdrop-blur-md transition-all flex items-center gap-2 shadow-[0_0_15px_rgba(0,0,0,0.8)]"
                        >
                          <Pencil size={14} />
                          <span>Rabiscar Tela</span>
                        </button>
                        <button 
                          onClick={() => { setIsOpen(false); setIsRecapturingAngle(true); window.dispatchEvent(new CustomEvent('feedback-recapture', { detail: true })); }}
                          className="px-3 py-2 bg-black/80 border border-white/20 hover:border-white/50 text-white/70 hover:text-white text-[10px] md:text-xs uppercase tracking-wider rounded backdrop-blur-md transition-all flex items-center gap-2 shadow-[0_0_15px_rgba(0,0,0,0.8)]"
                        >
                          <RefreshCcw size={14} />
                          <span className="hidden md:inline">Recapturar</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-white/20 flex flex-col items-center gap-2">
                      <ImageIcon size={24} />
                      <span className="text-[10px] uppercase">Sem imagem</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] md:text-xs uppercase tracking-wider text-white/40">
                  Descrição / Notas <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (error) setError(null);
                    if (showSubmitConfirm) setShowSubmitConfirm(false);
                  }}
                  placeholder="Descreva o que você está vendo ou qual o problema..."
                  className="w-full flex-1 min-h-[100px] md:min-h-[150px] bg-black/50 border border-white/10 rounded p-3 text-xs md:text-sm text-white focus:outline-none focus:border-[#00ffa3]/50 transition-colors resize-none font-mono"
                />
              </div>
            </div>

            {error && (
              <div className="text-red-400 text-[10px] md:text-xs font-mono bg-red-400/10 border border-red-400/20 p-2 rounded mt-4 shrink-0">
                {error}
              </div>
            )}

            {showCancelConfirm && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded flex flex-col gap-2 shrink-0 animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center gap-2 text-red-400 text-xs font-bold uppercase tracking-wider">
                  <AlertTriangle size={14} />
                  Tem certeza que deseja cancelar?
                </div>
                <div className="text-[10px] md:text-xs text-white/70 leading-relaxed">
                  Você já escreveu algo! Seu feedback é muito importante para melhorarmos a plataforma. Tem certeza de que não quer enviar?
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="px-3 py-1.5 bg-[#00ffa3]/10 hover:bg-[#00ffa3]/20 border border-[#00ffa3]/30 text-[#00ffa3] text-[10px] md:text-xs font-bold uppercase tracking-wider rounded transition-colors"
                  >
                    Voltar e Enviar
                  </button>
                  <button
                    onClick={() => {
                      setShowCancelConfirm(false);
                      setIsOpen(false);
                      setScreenshotData(null);
                      setDescription('');
                      setError(null);
                    }}
                    className="px-3 py-1.5 hover:bg-white/5 text-red-400 text-[10px] md:text-xs uppercase tracking-wider rounded transition-colors"
                  >
                    Sim, Descartar
                  </button>
                </div>
              </div>
            )}

            {showSubmitConfirm && !showCancelConfirm && (
              <div className="mt-4 p-3 bg-[#00ffa3]/10 border border-[#00ffa3]/30 rounded flex flex-col gap-2 shrink-0 animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center gap-2 text-[#00ffa3] text-xs font-bold uppercase tracking-wider">
                  <CheckCircle size={14} />
                  Confirmar envio
                </div>
                <div className="text-[10px] md:text-xs text-[#00ffa3]/70 leading-relaxed">
                  Sua captura de tela e descrição serão enviadas. Tem certeza que deseja enviar este feedback agora?
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    onClick={() => setShowSubmitConfirm(false)}
                    className="px-3 py-1.5 hover:bg-white/5 text-white/50 hover:text-white text-[10px] md:text-xs uppercase tracking-wider rounded transition-colors"
                  >
                    Revisar
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isUploading}
                    className="px-4 py-1.5 bg-[#00ffa3]/20 hover:bg-[#00ffa3]/30 border border-[#00ffa3]/50 text-[#00ffa3] text-[10px] md:text-xs font-bold uppercase tracking-wider rounded transition-colors flex items-center gap-2"
                  >
                    {isUploading ? (
                      <>
                        <span className="w-3 h-3 border-2 border-[#00ffa3]/20 border-t-[#00ffa3] rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send size={12} /> Confirmar Envio
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {!showCancelConfirm && !showSubmitConfirm && (
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10 shrink-0 mb-4 md:mb-0">
                <button
                  onClick={() => {
                    if (description.trim() !== '') {
                      setShowCancelConfirm(true);
                    } else {
                      handleClose();
                    }
                  }}
                  className="px-4 py-3 md:py-2 text-[10px] md:text-xs uppercase tracking-wider text-white/50 hover:text-white transition-colors"
                  disabled={isUploading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isUploading || !screenshotData || isCapturing}
                  className="px-6 py-3 md:py-2 bg-[#00ffa3] hover:bg-[#00ffa3]/80 border border-[#00ffa3] text-black text-[10px] md:text-xs font-bold uppercase tracking-widest rounded transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed flex-1 md:flex-none justify-center shadow-[0_0_20px_rgba(0,255,163,0.4)]"
                >
                  {isUploading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-[#00ffa3]/20 border-t-[#00ffa3] rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      Enviar Feedback
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {isAnnotating && screenshotData && (
        <FeedbackAnnotation
          initialImage={screenshotData}
          onConfirm={(editedImage) => {
            setScreenshotData(editedImage);
            setIsAnnotating(false);
          }}
          onCancel={() => setIsAnnotating(false)}
        />
      )}
    </div>
  );
}
